import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * ChipSelectFilter Component
 * Multi-select filter using chips or list layout with optional include/exclude mode
 */
import { Check as CheckIcon } from "@mui/icons-material";
import { Box, Chip, List, ListItemButton, ListItemText, TextField, ToggleButton, ToggleButtonGroup, Typography, } from "@mui/material";
import { useMemo, useState } from "react";
export const ChipSelectFilter = ({ options, value, onChange, mode = "simple", searchable = false, layout = "chips", singleSelect = false, }) => {
    const [activeMode, setActiveMode] = useState("include");
    const [searchQuery, setSearchQuery] = useState("");
    const getOptionValue = (option) => { var _a; return (_a = option.value) !== null && _a !== void 0 ? _a : option.label; };
    const isSelected = (option) => {
        const optionValue = getOptionValue(option);
        return value.included.includes(optionValue) || value.excluded.includes(optionValue);
    };
    const isIncluded = (optionValue) => value.included.includes(optionValue);
    const handleOptionClick = (option) => {
        const optionValue = getOptionValue(option);
        if (mode === "simple") {
            // Simple mode: toggle in included list
            if (value.included.includes(optionValue)) {
                onChange(Object.assign(Object.assign({}, value), { included: value.included.filter((v) => v !== optionValue) }));
            }
            else if (singleSelect) {
                onChange(Object.assign(Object.assign({}, value), { included: [optionValue] }));
            }
            else {
                onChange(Object.assign(Object.assign({}, value), { included: [...value.included, optionValue] }));
            }
        }
        else {
            // Include/Exclude mode: add to active list
            if (activeMode === "include") {
                if (!value.included.includes(optionValue)) {
                    onChange({
                        included: [...value.included, optionValue],
                        excluded: value.excluded.filter((v) => v !== optionValue),
                    });
                }
            }
            else {
                if (!value.excluded.includes(optionValue)) {
                    onChange({
                        included: value.included.filter((v) => v !== optionValue),
                        excluded: [...value.excluded, optionValue],
                    });
                }
            }
        }
    };
    const handleRemove = (optionValue) => {
        onChange({
            included: value.included.filter((v) => v !== optionValue),
            excluded: value.excluded.filter((v) => v !== optionValue),
        });
    };
    const selectedOptions = options.filter((opt) => isSelected(opt));
    const filteredOptions = useMemo(() => {
        if (!searchable || !searchQuery.trim())
            return options;
        const query = searchQuery.toLowerCase();
        return options.filter((opt) => opt.label.toLowerCase().includes(query));
    }, [options, searchable, searchQuery]);
    const availableOptions = useMemo(() => {
        return filteredOptions.filter((opt) => !isSelected(opt));
    }, [filteredOptions, value]);
    if (layout === "list") {
        return (_jsxs(Box, Object.assign({ sx: { display: "flex", flexDirection: "column", gap: 1, pt: 1 } }, { children: [selectedOptions.length > 0 && (_jsx(Box, Object.assign({ sx: { display: "flex", flexWrap: "wrap", gap: 0.5, px: 1 } }, { children: selectedOptions.map((option) => (_jsx(Chip, { label: option.label, size: "small", color: "primary", variant: "outlined", onDelete: () => handleRemove(getOptionValue(option)), sx: { fontSize: "11px" } }, getOptionValue(option)))) }))), searchable && (_jsx(TextField, { size: "small", fullWidth: true, placeholder: "Search functions...", value: searchQuery, onChange: (e) => setSearchQuery(e.target.value), sx: {
                        "& .MuiInputBase-input": {
                            fontSize: "13px",
                        },
                    } })), _jsx(List, Object.assign({ dense: true, disablePadding: true, sx: {
                        maxHeight: 200,
                        overflow: "auto",
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                    } }, { children: filteredOptions.map((option) => {
                        const optionValue = getOptionValue(option);
                        const selected = isSelected(option);
                        return (_jsxs(ListItemButton, Object.assign({ selected: selected, onClick: () => handleOptionClick(option), sx: { py: 0.5 } }, { children: [_jsx(ListItemText, { primary: option.label, primaryTypographyProps: { fontSize: "12px" } }), selected && _jsx(CheckIcon, { fontSize: "small", color: "primary" })] }), optionValue));
                    }) }))] })));
    }
    return (_jsxs(Box, Object.assign({ sx: { display: "flex", flexDirection: "column", gap: 1.5, pt: 2 } }, { children: [mode === "includeExclude" && (_jsxs(ToggleButtonGroup, Object.assign({ value: activeMode, exclusive: true, onChange: (_, newMode) => newMode && setActiveMode(newMode), size: "small", sx: {
                    "& .MuiToggleButton-root": {
                        px: 2,
                        py: 0.5,
                        fontSize: "12px",
                        textTransform: "none",
                    },
                } }, { children: [_jsx(ToggleButton, Object.assign({ value: "include", color: "primary" }, { children: "Include" })), _jsx(ToggleButton, Object.assign({ value: "exclude", color: "error" }, { children: "Exclude" }))] }))), selectedOptions.length > 0 && (_jsxs(Box, { children: [_jsx(Typography, Object.assign({ variant: "caption", color: "text.secondary", sx: { mb: 0.5, display: "block" } }, { children: "Selected" })), _jsx(Box, Object.assign({ sx: { display: "flex", flexWrap: "wrap", gap: 0.5 } }, { children: selectedOptions.map((option) => {
                            const optionValue = getOptionValue(option);
                            const included = isIncluded(optionValue);
                            return (_jsx(Chip, { label: option.label, size: "small", color: included ? "primary" : "error", variant: "outlined", onDelete: () => handleRemove(optionValue), sx: { fontSize: "11px" } }, optionValue));
                        }) }))] })), searchable && (_jsx(TextField, { size: "small", fullWidth: true, placeholder: "Search options...", value: searchQuery, onChange: (e) => setSearchQuery(e.target.value), sx: {
                    "& .MuiInputBase-input": {
                        fontSize: "13px",
                    },
                } })), availableOptions.length > 0 && (_jsxs(Box, { children: [selectedOptions.length > 0 && (_jsx(Typography, Object.assign({ variant: "caption", color: "text.secondary", sx: { mb: 0.5, display: "block" } }, { children: "Available" }))), _jsx(Box, Object.assign({ sx: { display: "flex", flexWrap: "wrap", gap: 0.5 } }, { children: availableOptions.map((option) => (_jsx(Chip, { label: option.label, size: "small", variant: "outlined", onClick: () => handleOptionClick(option), sx: {
                                fontSize: "11px",
                                cursor: "pointer",
                                "&:hover": {
                                    backgroundColor: "action.hover",
                                },
                            } }, getOptionValue(option)))) }))] }))] })));
};
export default ChipSelectFilter;
